import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Compass, Search, Shield, Sprout } from "lucide-react";
import { api, apiError, store } from "../../lib/api";
import { STATUT, OBJECTIF, isValidEmail, isValidPhone, formatPhone } from "../../data/options";
import { Field, SelectField } from "./FormBits";
import { Checkbox } from "../ui/checkbox";
import { Container, Reveal } from "./Motion";

const GOALS = [
  { icon: Compass, t: "Comprendre votre situation financière." },
  { icon: Search, t: "Identifier des possibilités d'optimisation." },
  { icon: Shield, t: "Explorer les moyens de protéger vos revenus." },
  { icon: Sprout, t: "Construire une stratégie adaptée à votre avenir." },
];

function initialForm() {
  const q = store.get("struktiv_q", {});
  return { full_name: q.full_name || "", email: q.email || "", phone: q.phone || "", statut: q.statut || "",
    objectif: q.objectif || "", message: "", consent_data: false, consent_marketing: false, website: "" };
}

function validate(f) {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Veuillez indiquer votre nom complet.";
  if (!isValidEmail(f.email)) e.email = "Adresse courriel invalide.";
  if (!isValidPhone(f.phone)) e.phone = "Numéro canadien à 10 chiffres requis.";
  if (!f.statut) e.statut = "Veuillez sélectionner votre statut.";
  if (!f.objectif) e.objectif = "Veuillez sélectionner un objectif.";
  if (!f.consent_data) e.consent_data = "Votre consentement est requis pour traiter votre demande.";
  return e;
}

const ConsultForm = ({ forfait, leadId }) => {
  const [f, setF] = useState(initialForm);
  const [err, setErr] = useState({});
  const [state, setState] = useState({ sending: false, done: false, error: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(f);
    setErr(e);
    if (Object.keys(e).length) return;
    setState({ sending: true, done: false, error: "" });
    try {
      await api.post("/consultations", { ...f, forfait_slug: forfait?.slug || null, lead_id: leadId || null });
      setState({ sending: false, done: true, error: "" });
    } catch (x) {
      setState({ sending: false, done: false, error: apiError(x) });
    }
  };

  if (state.done) {
    return (
      <div className="rounded-2xl bg-[#FFFDFB] p-8 text-[#242020]" data-testid="consultation-success">
        <CheckCircle2 className="h-10 w-10 text-[#650D23]" />
        <h3 className="mt-4 text-2xl">Merci, votre demande a bien été reçue.</h3>
        <p className="mt-3 text-[#242020]/80">Un membre de l'équipe STRUKTIV communiquera avec vous pour convenir d'un moment de rencontre. Aucun rendez-vous n'est encore fixé.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-5 rounded-2xl bg-[#FFFDFB] p-6 text-[#242020] sm:p-8" data-testid="consultation-form">
      {forfait && (
        <p className="rounded-xl bg-[#FAF6F1] px-4 py-3 text-sm" data-testid="consultation-attached-forfait">
          Forfait joint à votre demande : <strong className="text-[#650D23]">{forfait.name}</strong>
        </p>
      )}
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} className="hidden" aria-hidden="true" />
      <Field label="Nom complet" required error={err.full_name} htmlFor="c-name" errorTestid="consultation-error-name">
        <input id="c-name" className="field-input" value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} aria-invalid={!!err.full_name} data-testid="consultation-name-input" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Courriel" required error={err.email} htmlFor="c-email" errorTestid="consultation-error-email">
          <input id="c-email" type="email" className="field-input" value={f.email} onChange={(e) => set("email")(e.target.value)} aria-invalid={!!err.email} data-testid="consultation-email-input" />
        </Field>
        <Field label="Téléphone" required error={err.phone} htmlFor="c-phone" errorTestid="consultation-error-phone">
          <input id="c-phone" type="tel" placeholder="(514) 123-4567" className="field-input" value={f.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} aria-invalid={!!err.phone} data-testid="consultation-phone-input" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Statut professionnel" required error={err.statut} htmlFor="c-statut">
          <SelectField id="c-statut" value={f.statut} onChange={set("statut")} options={STATUT} error={err.statut} testid="consultation-statut-select" />
        </Field>
        <Field label="Objectif principal" required error={err.objectif} htmlFor="c-obj">
          <SelectField id="c-obj" value={f.objectif} onChange={set("objectif")} options={OBJECTIF} error={err.objectif} testid="consultation-objectif-select" />
        </Field>
      </div>
      <Field label="Message (facultatif)" htmlFor="c-msg">
        <textarea id="c-msg" rows={3} className="field-input" value={f.message} onChange={(e) => set("message")(e.target.value)} data-testid="consultation-message-input" />
      </Field>
      <ConsentBoxes f={f} set={set} err={err} prefix="consultation" />
      {state.error && <p className="text-sm text-[#b42318]" role="alert" data-testid="consultation-server-error">{state.error}</p>}
      <button type="submit" className="btn-primary w-full" disabled={state.sending} data-testid="consultation-submit-button">
        {state.sending ? "Envoi en cours…" : "PARLER À UN CONSEILLER"} <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  );
};

export const ConsentBoxes = ({ f, set, err, prefix }) => (
  <div className="space-y-3 text-sm">
    <label className="flex items-start gap-3">
      <Checkbox checked={f.consent_data} onCheckedChange={(v) => set("consent_data")(v === true)} data-testid={`${prefix}-consent-data`} className="mt-0.5" />
      <span>J'accepte que STRUKTIV recueille et utilise mes renseignements personnels pour traiter ma demande, conformément à la{" "}
        <Link to="/politique-de-confidentialite" target="_blank" className="text-[#650D23] underline">politique de confidentialité</Link>. <span className="text-[#650D23]">*</span></span>
    </label>
    {err.consent_data && <p className="text-[#b42318]" role="alert" data-testid={`${prefix}-error-consent`}>{err.consent_data}</p>}
    <label className="flex items-start gap-3">
      <Checkbox checked={f.consent_marketing} onCheckedChange={(v) => set("consent_marketing")(v === true)} data-testid={`${prefix}-consent-marketing`} className="mt-0.5" />
      <span>J'accepte de recevoir des communications promotionnelles de STRUKTIV (facultatif, retrait possible en tout temps).</span>
    </label>
  </div>
);

export const ConsultationSection = ({ forfait = null, leadId = null }) => (
  <section id="contact" className="relative overflow-hidden bg-[#650D23] text-[#FFFDFB] grain" data-testid="consultation-section">
    <img src="/img/pont2.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25 mix-blend-luminosity" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#4A0C1B]/90 via-[#650D23]/75 to-[#650D23]/60" aria-hidden="true" />
    <Container className="relative grid gap-14 py-20 lg:grid-cols-2 lg:py-28">
      <Reveal className="space-y-8">
        <p className="eyebrow !text-[#EDE3D9]">Consultation gratuite</p>
        <h2 className="text-4xl leading-tight sm:text-5xl">Votre première consultation est gratuite.</h2>
        <p className="max-w-lg text-lg text-[#EDE3D9]">Lors de notre première rencontre, nous prendrons le temps de comprendre votre situation, vos objectifs et les possibilités qui s'offrent à vous.</p>
        <ul className="grid gap-4 sm:grid-cols-2">
          {GOALS.map(({ icon: I, t }) => (
            <li key={t} className="flex items-start gap-3 rounded-xl border border-[#FFFDFB]/15 p-4">
              <I className="mt-0.5 h-5 w-5 shrink-0 text-[#EDE3D9]" /><span className="text-sm">{t}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1}><ConsultForm forfait={forfait} leadId={leadId} /></Reveal>
    </Container>
  </section>
);
